/**
 * Timeline column generators for each zoom level
 */

import { MS_PER_DAY, MS_PER_HOUR } from './constants';
import { calculatePosition } from './dateCalculations';
import { formatDate, formatHour, isWeekend, formatDateRange, isCurrentPeriod } from './formatUtils';

/**
 * Generate hour columns (for hour and hour-detail zoom levels)
 * Uses calculatePosition for consistent alignment with day/month views
 */
function generateHourColumns(startDate, totalDays, zoomLevel) {
  const columns = [];
  const groups = [];
  let currentDay = null;
  const totalHours = Math.max(1, totalDays * 24);
  
  for (let i = 0; i < totalHours; i++) {
    const date = new Date(startDate.getTime() + i * MS_PER_HOUR);
    const nextHour = new Date(date.getTime() + MS_PER_HOUR);
    const hour = date.getHours();
    const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    
    // Track day groups
    if (dayKey !== currentDay) {
      if (currentDay !== null && groups.length > 0) {
        groups[groups.length - 1].endIndex = columns.length - 1;
      }
      groups.push({
        label: formatDate(date, 'dayFull'),
        startIndex: columns.length,
        endIndex: columns.length,
      });
      currentDay = dayKey;
    }
    
    // Use calculatePosition for consistent alignment across zoom levels
    const position = calculatePosition(date, startDate, totalDays);
    const endPosition = calculatePosition(nextHour, startDate, totalDays);
    const width = endPosition - position;
    
    columns.push({
      date,
      label: formatHour(hour, zoomLevel === 'hour-detail'),
      position,
      width,
      isWeekend: isWeekend(date),
      isWorkingHour: hour >= 9 && hour < 18,
      isCurrent: isCurrentPeriod(date, 'hour'),
    });
  }
  
  if (groups.length > 0) {
    groups[groups.length - 1].endIndex = columns.length - 1;
  }
  
  return { columns, groups };
}

/**
 * Generate day columns (for day and day-detail zoom levels)
 * Uses calculatePosition for consistent alignment with month/quarter/year views
 * Month groups are calculated from actual calendar month boundaries
 */
function generateDayColumns(startDate, totalDays, zoomLevel) {
  const columns = [];
  
  for (let i = 0; i < totalDays; i++) {
    const date = new Date(startDate.getTime() + i * MS_PER_DAY);
    date.setHours(0, 0, 0, 0); // Ensure midnight
    const nextDate = new Date(date.getTime() + MS_PER_DAY);
    
    const label = zoomLevel === 'day-detail' 
      ? formatDate(date, 'day')
      : date.getDate().toString();
    
    // Use calculatePosition for consistent alignment across zoom levels
    const position = calculatePosition(date, startDate, totalDays);
    const endPosition = calculatePosition(nextDate, startDate, totalDays);
    const width = endPosition - position;
    
    columns.push({
      date,
      label,
      position,
      width,
      isWeekend: isWeekend(date),
      isFirstOfMonth: date.getDate() === 1,
      isCurrent: isCurrentPeriod(date, 'day'),
    });
  }
  
  // Calculate end date from totalDays
  const endDate = new Date(startDate.getTime() + totalDays * MS_PER_DAY);
  
  // Generate month groups based on actual calendar month boundaries
  const groups = generateCalendarMonthGroups(startDate, endDate, totalDays);
  
  return { columns, groups };
}

/**
 * Generate week columns
 * Month groups are calculated from actual calendar month boundaries, not week indices
 */
function generateWeekColumns(startDate, endDate, totalDays) {
  const columns = [];
  let currentDate = new Date(startDate);
  currentDate.setHours(0, 0, 0, 0); // Normalize to midnight
  currentDate.setDate(currentDate.getDate() - currentDate.getDay()); // Go to Sunday
  
  while (currentDate < endDate) {
    const weekStart = new Date(currentDate);
    const weekEnd = new Date(currentDate);
    weekEnd.setDate(weekEnd.getDate() + 6); // End of week (Saturday)
    
    const rawPosition = calculatePosition(weekStart, startDate, totalDays);
    const nextWeekStart = new Date(currentDate);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);
    const endPosition = calculatePosition(nextWeekStart, startDate, totalDays);
    
    const position = Math.max(0, rawPosition);
    const width = endPosition - position;
    
    columns.push({
      date: weekStart,
      label: formatDateRange(weekStart, weekEnd),
      position: position,
      width: Math.max(0, Math.min(width, 100 - position)),
      isWeekend: false,
      isCurrent: isCurrentPeriod(weekStart, 'week'),
    });
    
    currentDate.setDate(currentDate.getDate() + 7);
  }
  
  // Generate month groups based on actual calendar month boundaries
  const groups = generateCalendarMonthGroups(startDate, endDate, totalDays);
  
  return { columns, groups };
}

/**
 * Generate month groups based on actual calendar month boundaries
 * Used by week view to ensure month headers align with actual months
 */
function generateCalendarMonthGroups(startDate, endDate, totalDays) {
  const groups = [];
  
  // Start at first of the month containing startDate
  let currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1, 0, 0, 0, 0);
  
  while (currentDate < endDate) {
    const monthStart = new Date(currentDate);
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1, 0, 0, 0, 0);
    
    const rawPosition = calculatePosition(monthStart, startDate, totalDays);
    const endPosition = calculatePosition(nextMonth, startDate, totalDays);
    
    const position = Math.max(0, rawPosition);
    const width = endPosition - position;
    
    groups.push({
      label: formatDate(monthStart, 'monthLong'),
      position: position,
      width: Math.max(0, Math.min(width, 100 - position)),
    });
    
    currentDate = nextMonth;
  }
  
  return groups;
}

/**
 * Generate month columns
 */
function generateMonthColumns(startDate, endDate, totalDays) {
  const columns = [];
  const groups = [];
  // Start at first of the month containing startDate, at midnight
  let currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1, 0, 0, 0, 0);
  let currentYear = null;
  let monthIndex = 0;
  
  while (currentDate < endDate) {
    const monthStart = new Date(currentDate);
    monthStart.setHours(0, 0, 0, 0); // Ensure midnight
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1, 0, 0, 0, 0);
    
    const rawPosition = calculatePosition(monthStart, startDate, totalDays);
    const endPosition = calculatePosition(nextMonth, startDate, totalDays);
    
    // Clamp position to 0 and adjust width accordingly for partial first month
    const position = Math.max(0, rawPosition);
    const width = endPosition - position;
    
    const yearKey = monthStart.getFullYear();
    if (yearKey !== currentYear) {
      if (currentYear !== null && groups.length > 0) {
        groups[groups.length - 1].endIndex = monthIndex - 1;
      }
      groups.push({
        label: yearKey.toString(),
        startIndex: monthIndex,
        endIndex: monthIndex,
      });
      currentYear = yearKey;
    }
    
    columns.push({
      date: monthStart,
      label: formatDate(monthStart, 'monthOnly'),
      position: position,
      width: Math.max(0, Math.min(width, 100 - position)),
      isWeekend: false,
      isCurrent: isCurrentPeriod(monthStart, 'month'),
    });
    
    currentDate = nextMonth;
    monthIndex++;
  }
  
  if (groups.length > 0) {
    groups[groups.length - 1].endIndex = monthIndex - 1;
  }
  
  return { columns, groups };
}

/**
 * Generate quarter columns
 */
function generateQuarterColumns(startDate, endDate, totalDays) {
  const columns = [];
  const groups = [];
  // Start at first day of the quarter containing startDate, at midnight
  let currentDate = new Date(startDate.getFullYear(), Math.floor(startDate.getMonth() / 3) * 3, 1, 0, 0, 0, 0);
  let currentYear = null;
  let quarterIndex = 0;
  
  while (currentDate < endDate) {
    const quarterStart = new Date(currentDate);
    quarterStart.setHours(0, 0, 0, 0); // Ensure midnight
    const nextQuarter = new Date(currentDate.getFullYear(), currentDate.getMonth() + 3, 1, 0, 0, 0, 0);
    
    const rawPosition = calculatePosition(quarterStart, startDate, totalDays);
    const endPosition = calculatePosition(nextQuarter, startDate, totalDays);
    
    // Clamp position to 0 and adjust width accordingly for partial first quarter
    const position = Math.max(0, rawPosition);
    const width = endPosition - position;
    
    const yearKey = quarterStart.getFullYear();
    if (yearKey !== currentYear) {
      if (currentYear !== null && groups.length > 0) {
        groups[groups.length - 1].endIndex = quarterIndex - 1;
      }
      groups.push({
        label: yearKey.toString(),
        startIndex: quarterIndex,
        endIndex: quarterIndex,
      });
      currentYear = yearKey;
    }
    
    const quarter = Math.floor(quarterStart.getMonth() / 3) + 1;
    columns.push({
      date: quarterStart,
      label: `Q${quarter}`,
      position: position,
      width: Math.max(0, Math.min(width, 100 - position)),
      isWeekend: false,
      isCurrent: isCurrentPeriod(quarterStart, 'quarter'),
    });
    
    currentDate = nextQuarter;
    quarterIndex++;
  }
  
  if (groups.length > 0) {
    groups[groups.length - 1].endIndex = quarterIndex - 1;
  }
  
  return { columns, groups };
}

/**
 * Generate year columns
 */
function generateYearColumns(startDate, endDate, totalDays) {
  const columns = [];
  // Start at Jan 1 of the year containing startDate, at midnight
  let currentDate = new Date(startDate.getFullYear(), 0, 1, 0, 0, 0, 0);
  
  while (currentDate < endDate) {
    const yearStart = new Date(currentDate);
    yearStart.setHours(0, 0, 0, 0); // Ensure midnight
    const nextYear = new Date(currentDate.getFullYear() + 1, 0, 1, 0, 0, 0, 0);
    
    const rawPosition = calculatePosition(yearStart, startDate, totalDays);
    const endPosition = calculatePosition(nextYear, startDate, totalDays);
    
    // Clamp position to 0 and adjust width accordingly for partial first year
    const position = Math.max(0, rawPosition);
    const width = endPosition - position;
    
    columns.push({
      date: yearStart,
      label: yearStart.getFullYear().toString(),
      position: position,
      width: Math.max(0, Math.min(width, 100 - position)),
      isWeekend: false,
      isCurrent: isCurrentPeriod(yearStart, 'year'),
    });
    
    currentDate = nextYear;
  }
  
  // Year view has no groups (no secondary tier)
  return { columns, groups: [] };
}

/**
 * Calculate group positions from columns
 * Skips groups that already have position/width set directly (like week view's month groups)
 */
function calculateGroupPositions(columns, groups) {
  groups.forEach(group => {
    // Skip if position/width already calculated directly (e.g., from generateCalendarMonthGroups)
    if (group.position !== undefined && group.width !== undefined) {
      return;
    }
    // Calculate from column indices
    if (columns[group.startIndex] && columns[group.endIndex]) {
      group.position = columns[group.startIndex].position;
      const endCol = columns[group.endIndex];
      group.width = (endCol.position + endCol.width) - group.position;
    }
  });
}

/**
 * Generate timeline columns with a two-tier header approach
 */
export function generateTimelineColumns(startDate, endDate, totalDays, zoomLevel = 'day') {
  let result;
  
  switch (zoomLevel) {
    case 'hour-detail':
    case 'hour':
      result = generateHourColumns(startDate, totalDays, zoomLevel);
      break;
    case 'day-detail':
    case 'day':
      result = generateDayColumns(startDate, totalDays, zoomLevel);
      break;
    case 'week':
      result = generateWeekColumns(startDate, endDate, totalDays);
      break;
    case 'month':
      result = generateMonthColumns(startDate, endDate, totalDays);
      break;
    case 'quarter':
      result = generateQuarterColumns(startDate, endDate, totalDays);
      break;
    case 'year':
      result = generateYearColumns(startDate, endDate, totalDays);
      break;
    default:
      result = generateDayColumns(startDate, totalDays, 'day');
  }
  
  // Calculate group positions
  calculateGroupPositions(result.columns, result.groups);
  
  return result;
}
